const API_KEY_ENDPOINT = import.meta.env.VITE_SIGNALS_API_KEY_ENDPOINT || ''
const ADMIN_SHARED_SECRET = import.meta.env.VITE_ADMIN_SHARED_SECRET || ''

export interface GenerateApiKeyParams {
  organizationId: string
  name: string
  expiresIn?: string // '30d', '90d', '180d', '1y', or undefined for never
  claims?: Record<string, unknown>
}

export interface GenerateApiKeyResult {
  success: boolean
  token?: string
  keyId?: string
  expiresAt?: string
  error?: string
}

export interface ValidateApiKeyResult {
  valid: boolean
  organizationId?: string
  name?: string
  claims?: Record<string, unknown>
  error?: string
}

/**
 * Generate a new JWT-based API key via the Signals service
 */
export async function generateApiKey(params: GenerateApiKeyParams): Promise<GenerateApiKeyResult> {
  const { organizationId, name, expiresIn, claims = {} } = params

  if (!API_KEY_ENDPOINT) {
    // Fallback: generate a simple token locally for development
    console.warn('VITE_SIGNALS_API_KEY_ENDPOINT not configured, generating local token')
    return generateLocalApiKey(params)
  }

  if (!ADMIN_SHARED_SECRET) {
    console.error('VITE_ADMIN_SHARED_SECRET not configured')
    return { success: false, error: 'Server configuration error' }
  }

  try {
    const response = await fetch(API_KEY_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shared-Secret': ADMIN_SHARED_SECRET,
      },
      body: JSON.stringify({
        action: 'generate',
        organizationId,
        name,
        expiresIn,
        claims,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('Error from Signals service:', errorData)
      return { success: false, error: 'Failed to generate API key' }
    }

    const result = await response.json()
    return {
      success: true,
      token: result.token,
      keyId: result.keyId,
      expiresAt: result.expiresAt,
    }
  } catch (error) {
    console.error('Error generating API key:', error)
    return { success: false, error: 'Network error' }
  }
}

/**
 * Validate a JWT-based API key
 */
export async function validateApiKey(token: string): Promise<ValidateApiKeyResult> {
  if (!API_KEY_ENDPOINT) {
    return { valid: false, error: 'API key endpoint not configured' }
  }

  if (!ADMIN_SHARED_SECRET) {
    return { valid: false, error: 'Server configuration error' }
  }

  try {
    const response = await fetch(API_KEY_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shared-Secret': ADMIN_SHARED_SECRET,
      },
      body: JSON.stringify({
        action: 'validate',
        token,
      }),
    })

    if (!response.ok) {
      return { valid: false, error: 'Invalid token' }
    }

    const result = await response.json()
    return {
      valid: result.valid,
      organizationId: result.organizationId,
      name: result.name,
      claims: result.claims,
    }
  } catch (error) {
    console.error('Error validating API key:', error)
    return { valid: false, error: 'Network error' }
  }
}

/**
 * Generate a local API key for development (when signals endpoint not configured)
 * This creates a stateless JWT-like token that encodes the org info
 */
function generateLocalApiKey(params: GenerateApiKeyParams): GenerateApiKeyResult {
  const { organizationId, name, expiresIn } = params
  
  // Create a simple base64-encoded token with org info
  const keyId = crypto.randomUUID()
  const now = Date.now()
  
  let expiresAt: number | undefined
  if (expiresIn) {
    const match = expiresIn.match(/^(\d+)([dmyh])$/)
    if (match) {
      const value = parseInt(match[1])
      const unit = match[2]
      let ms = 0
      
      switch (unit) {
        case 'h': ms = value * 3600000; break
        case 'd': ms = value * 86400000; break
        case 'm': ms = value * 2592000000; break
        case 'y': ms = value * 31536000000; break
      }
      
      expiresAt = now + ms
    }
  }
  
  // Create payload (timestamps in seconds for JWT standard)
  const payload = {
    jti: keyId,
    sub: organizationId,
    org_id: organizationId,
    name,
    iat: Math.floor(now / 1000),
    exp: expiresAt ? Math.floor(expiresAt / 1000) : undefined,
    iss: 'signals-dev',
  }
  
  // Encode as base64url (JWT standard)
  const base64url = (str: string) => btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  
  const header = base64url(JSON.stringify({ alg: 'HS256', typ: 'JWT' }))
  const body = base64url(JSON.stringify(payload))
  // Dev signature - not cryptographically valid but looks like a real JWT
  const signature = base64url(`dev_${keyId.substring(0, 8)}`)
  
  const token = `${header}.${body}.${signature}`
  
  return {
    success: true,
    token,
    keyId,
    expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
  }
}
